import React from 'react';
import { ColorPicker } from 'antd';
import type { Color } from 'antd/es/color-picker';
import { BoldOutlined, ItalicOutlined, UnderlineOutlined, BgColorsOutlined } from '@ant-design/icons';
import { Redo2, Undo2 } from 'lucide-react';
import { $getSelection, $isRangeSelection } from 'lexical';
import { $patchStyleText } from '@lexical/selection';
import { useEditor } from './editor';

export const Toolbar: React.FC = () => {
  const { commands, activeStates, editor } = useEditor();
  const [hasSelection, setHasSelection] = React.useState(false);
  const [currentColor, setCurrentColor] = React.useState<string>('#000000');

  React.useEffect(() => {
    if (!editor) return;

    // Trackear estado de selección para habilitar/deshabilitar botones del toolbar
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        setHasSelection($isRangeSelection(selection) && !selection.isCollapsed());
      });
    });
  }, [editor]);

  /**
   * Handler para color
   */
  const handleColorChange = (color: Color) => {
    const hexColor = color.toHexString();

    if (!editor) return;

    editor.update(() => {
      const selection = $getSelection();

      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        return;
      }

      // Aplicar color sin validaciones
      $patchStyleText(selection, { color: hexColor });
      setCurrentColor(hexColor);
    });
  };

  return (
    <div className='rich-text-toolbar'>
      {/* Color Picker */}
      <ColorPicker
        value={currentColor}
        onChange={handleColorChange}
        size='small'
        showText={false}
        disabledAlpha
        disabled={!hasSelection}
        presets={[
          {
            label: 'Colores recomendados',
            colors: [
              '#FF7DCB',
              '#24BA4E',
              '#00237C',
              '#0039E3',
              '#2D5FFF',
              '#5A89FF',
              '#CFE3FF',
              '#E9F3FF',
              '#E7F2EB',
              '#EDEEEF',
              '#FFFFFF',
              '#343F49',
              '#000000'
            ]
          }
        ]}
      >
        <button
          type='button'
          className={`rich-text-toolbar-button`}
          disabled={!hasSelection}
          title='Color de texto'
          aria-label='Aplicar color'
        >
          <BgColorsOutlined />
        </button>
      </ColorPicker>

      {/* Botón BOLD */}
      <button
        type='button'
        className={`rich-text-toolbar-button ${activeStates.bold ? 'active' : ''}`}
        onClick={() => commands.toggleBold()}
        title='Bold (Ctrl+B)'
        aria-label='Aplicar negrita'
        disabled={!hasSelection}
      >
        <BoldOutlined />
      </button>

      {/* Botón ITALIC */}
      <button
        type='button'
        className={`rich-text-toolbar-button ${activeStates.italic ? 'active' : ''}`}
        onClick={() => commands.toggleItalic()}
        title='Italic (Ctrl+I)'
        aria-label='Aplicar cursiva'
        disabled={!hasSelection}
      >
        <ItalicOutlined />
      </button>

      {/* Botón UNDERLINE */}
      <button
        type='button'
        className={`rich-text-toolbar-button ${activeStates.underline ? 'active' : ''}`}
        onClick={() => commands.toggleUnderline()}
        title='Underline (Ctrl+U)'
        aria-label='Aplicar subrayado'
        disabled={!hasSelection}
      >
        <UnderlineOutlined />
      </button>

      <div className='rich-text-toolbar-separator' />

      {/* Undo/Redo */}
      <button
        type='button'
        className='rich-text-toolbar-button'
        onClick={() => commands.undo()}
        disabled={!activeStates.canUndo}
        title='Deshacer (Ctrl+Z)'
        aria-label='Deshacer'
      >
        <Undo2 width={20} color='#B4B6B4' />
      </button>

      <button
        type='button'
        className='rich-text-toolbar-button'
        onClick={() => commands.redo()}
        disabled={!activeStates.canRedo}
        title='Rehacer (Ctrl+Y)'
        aria-label='Rehacer'
      >
        <Redo2 width={20} color='#B4B6B4' />
      </button>
    </div>
  );
};
